import Color from 'colorjs.io'
import { useMemo } from 'react'

export type ColorHex = string
export type ColorPalette = string[]

export interface ColorPaletteState {
  websitePalette: ColorPalette
  generatedPalettes: ColorPalette[]
  selectedPaletteIndex: number
}

export const toHex = (input: string): ColorHex =>
  new Color(input).to('srgb').toString({ format: 'hex' }).toUpperCase() as ColorHex

const L = (c: ColorHex) => new Color(c).oklch.l
const CR = (a: ColorHex, b: ColorHex) => new Color(a).contrast(b, 'WCAG21') as number
const isGrey = (c: ColorHex) => {
  const { r, g, b } = new Color(c).srgb
  return Math.abs(r - g) < 0.03 && Math.abs(g - b) < 0.03
}

const clamp = (v: number, min = 0, max = 1) => Math.max(min, Math.min(max, v))

const tweak = (base: ColorHex, { dl = 1, dc = 1, dh = 0 }: { dl?: number; dc?: number; dh?: number }): ColorHex => {
  const { l, c, h } = new Color(base).oklch
  const L2 = clamp(l * dl, 0.05, 0.95)
  const C2 = clamp(c * dc, 0, 0.4)
  const H2 = ((h ?? 0) + dh + 360) % 360
  return new Color('oklch', [L2, C2, H2]).to('srgb').toString({ format: 'hex' }) as ColorHex
}

const pickBaseColour = (palette: ColorPalette): ColorHex =>
  [...palette].sort((a, b) => new Color(b).oklch.c - new Color(a).oklch.c).find((c) => !isGrey(c)) || palette[0]

const exactPalette = (src: ColorPalette, lightBg: boolean): ColorPalette => {
  if (src.length <= 1) return [...src]

  const original = [...src]
  const bg = lightBg
    ? src.reduce((best, c) => (L(c) > L(best) ? c : best))
    : src.reduce((best, c) => (L(c) < L(best) ? c : best))

  const remaining = src.filter((c) => c !== bg)
  if (remaining.length === 1) return [bg, remaining[0]]

  const text = remaining.reduce((best, c) => (CR(c, bg) > CR(best, bg) ? c : best))
  let pool = remaining.filter((c) => c !== text)
  let accent = pool.find((c) => !isGrey(c)) ?? pool[0] ?? text
  const extras = original.filter((c) => ![bg, accent, text].includes(c))
  return [bg, accent, text, ...extras] as ColorPalette
}

const pastelPalette = (base: ColorHex): ColorPalette => {
  const background = tweak(base, { dl: 2.0, dc: 0.25 })
  const accent = tweak(base, { dl: 1.6, dc: 0.45, dh: 30 })
  const text = tweak(base, { dl: 0.4, dc: 0.9 })
  const extra1 = tweak(base, { dl: 1.3, dc: 0.35, dh: -30 })
  const extra2 = tweak(base, { dl: 1.15, dc: 0.35, dh: 60 })
  return [background, accent, text, extra1, extra2].map(toHex)
}

const splitBrandPalette = (base: ColorHex): ColorPalette => {
  const background = tweak(base, { dl: 2.1, dc: 0.18 })
  const accent = tweak(base, { dl: 1.1, dc: 1.2, dh: 30 })
  const text = tweak(base, { dl: 0.3, dc: 0.8 })
  const extra1 = tweak(base, { dl: 1.1, dc: 1.0, dh: -30 })
  const extra2 = tweak(base, { dl: 0.8, dc: 0.5 })
  return [background, accent, text, extra1, extra2].map(toHex)
}

export const generatePalettes = (websiteColors: ColorPalette): ColorPalette[] => {
  const src = [...new Set(websiteColors.map(toHex))]
  if (src.length < 2) return []

  const light = exactPalette(src, true)
  const dark = exactPalette(src, false)
  const base = pickBaseColour(src)
  const pastel = pastelPalette(base)
  const splitBrand = splitBrandPalette(base)

  return [light, dark, pastel, splitBrand]
}

export const useOptimizedPalettes = ({ websitePalette, generatedPalettes }: ColorPaletteState): ColorPalette[] => {
  return useMemo(() => {
    const built = generatePalettes(websitePalette)
    return built.length === 4 ? built : [...built, ...generatedPalettes]
  }, [websitePalette, generatedPalettes])
}

export const adjustLightness = (c: ColorHex, f: number) => tweak(toHex(c), { dl: f })
export const adjustHue = (c: ColorHex, d: number) => tweak(toHex(c), { dh: d })
export const adjustSaturation = (c: ColorHex, f: number) => tweak(toHex(c), { dc: f })

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
  })
